import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { formatPhoneNumber } from '@/utils/phoneFormat';
import { formatCurrency } from '@/utils/currencyFormat';
import { z } from 'zod';

const taxLiabilitySchema = z.object({
  formType: z.string().default('Zero Your IRS Tax Obligation'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().min(1, 'Phone number is required'),
  email: z.string().email('Please enter a valid email address'),
  taxesOwed: z.string().min(1, 'Amount of taxes owed is required'),
  state: z.string().min(1, 'Please select your state'),
  workedInDC: z.string().min(1, 'Please select an option'),
});

type TaxLiabilityFormData = z.infer<typeof taxLiabilitySchema>;

interface TaxLiabilityFormProps {
  onSuccess?: () => void;
}

const TaxLiabilityForm = ({ onSuccess }: TaxLiabilityFormProps = {}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState<TaxLiabilityFormData>({
    formType: 'Zero Your IRS Tax Obligation',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    taxesOwed: '',
    state: '',
    workedInDC: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof TaxLiabilityFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  const handleInputChange = (field: keyof TaxLiabilityFormData, value: string) => {
    // Format phone number as user types
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }
    // Format currency fields as user types
    if (field === 'taxesOwed') {
      value = formatCurrency(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Close any open dropdowns
    document.body.click();

    try {
      const validatedData = taxLiabilitySchema.parse(formData);
      setErrors({});
      
      // Send data to Zapier webhook
      const response = await fetch('https://hooks.zapier.com/hooks/catch/21776725/u1pdpuq/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          ...validatedData,
          timestamp: new Date().toISOString(),
          source: window.location.origin
        }),
      });

      // Close the form first if onSuccess callback is provided
      if (onSuccess) {
        onSuccess();
      }

      // Small delay to let the form close before showing notification
      setTimeout(() => {
        toast({
          title: "Request Submitted",
          description: "Your tax liability removal request has been submitted successfully. We'll contact you soon.",
        });
      }, 300);

      // Reset form after successful submission
      handleReset();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof TaxLiabilityFormData, string>> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as keyof TaxLiabilityFormData] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: "Error",
          description: "Failed to submit your request. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      formType: 'Zero Your IRS Tax Obligation',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      taxesOwed: '',
      state: '',
      workedInDC: '',
    });
    setErrors({});
  };

  return (
    <div className="max-w-2xl -mx-2 md:mx-auto">
      <Card className="w-full">
      <CardHeader>
        <CardTitle className="font-rockwell text-2xl text-primary">
          Tax Liability Removal Application
        </CardTitle>
        <CardDescription>
          Please provide your information to begin the tax liability removal process.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleFormSubmit} className="space-y-6">
          <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={errors.firstName ? 'border-destructive' : ''}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={errors.lastName ? 'border-destructive' : ''}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={errors.phone ? 'border-destructive' : ''}
            />
            {errors.phone && (
              <p className="text-sm text-destructive">{errors.phone}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="taxesOwed">Amount of Taxes Owed to IRS *</Label>
            <Input
              id="taxesOwed"
              type="text"
              placeholder="e.g., $10,000"
              value={formData.taxesOwed}
              onChange={(e) => handleInputChange('taxesOwed', e.target.value)}
              className={errors.taxesOwed ? 'border-destructive' : ''}
            />
            {errors.taxesOwed && (
              <p className="text-sm text-destructive">{errors.taxesOwed}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="state">State *</Label>
            <Select onValueChange={(value) => handleInputChange('state', value)}>
              <SelectTrigger className={errors.state ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select your state" />
              </SelectTrigger>
              <SelectContent>
                {states.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state && (
              <p className="text-sm text-destructive">{errors.state}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="workedInDC">Have you ever earned money or worked in Washington DC? *</Label>
            <Select onValueChange={(value) => handleInputChange('workedInDC', value)}>
              <SelectTrigger className={errors.workedInDC ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
            {errors.workedInDC && (
              <p className="text-sm text-destructive">{errors.workedInDC}</p>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              className="flex-1"
            >
              Reset
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Processing...' : 'Submit'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
    </div>
  );
};

export default TaxLiabilityForm;